const pageTimelineNav = {
    navs: [],
    isListening: false,
    ticking: false,
    stackedViewportQuery: '(width < 48rem)',

    init() {
        this.navs = Array.from(document.querySelectorAll('[data-page-timeline-nav]'));

        this.navs.forEach((nav) => this.setupNav(nav));

        if (!this.navs.length || this.isListening) {
            return;
        }

        window.addEventListener('resize', () => {
            this.navs.forEach((nav) => this.setTriggerTop(nav));
            this.requestUpdate();
        });
        window.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
        this.isListening = true;
        this.update();
    },

    setupNav(nav) {
        const scroller = this.getScroller(nav);

        this.setTriggerTop(nav);
        this.setOverflowState(nav);

        if (scroller && !scroller.dataset.timelineNavScrollerReady) {
            scroller.addEventListener('scroll', () => this.setOverflowState(nav), { passive: true });
            scroller.dataset.timelineNavScrollerReady = 'true';
        }

        if (!nav.dataset.timelineNavLinksReady) {
            nav.addEventListener('click', (event) => this.handleLinkClick(event, nav));
            nav.dataset.timelineNavLinksReady = 'true';
        }
    },

    setTriggerTop(nav) {
        nav.classList.remove('is-stuck');
        const triggerTop = nav.getBoundingClientRect().top + window.scrollY;

        nav.dataset.triggerTop = String(Math.max(0, triggerTop));
    },

    getScroller(nav) {
        return nav.querySelector('.page-timeline-nav__inner');
    },

    getItems(nav) {
        return Array.from(nav.querySelectorAll('.page-timeline-nav__link[href^="#"]'))
            .map((link) => {
                const id = decodeURIComponent(link.hash.slice(1));
                const target = document.getElementById(id);

                return target ? { id, link, target } : null;
            })
            .filter(Boolean);
    },

    setOverflowState(nav) {
        const scroller = this.getScroller(nav);

        if (!scroller) {
            return;
        }

        const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1;
        const isAtStart = scroller.scrollLeft <= 1;
        const isAtEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

        scroller.classList.toggle('has-overflow', hasOverflow);
        scroller.classList.toggle('is-at-start', hasOverflow && isAtStart);
        scroller.classList.toggle('is-at-end', hasOverflow && isAtEnd);
    },

    handleLinkClick(event, nav) {
        if (!(event.target instanceof Element)) {
            return;
        }

        const link = event.target.closest('.page-timeline-nav__link[href^="#"]');

        if (!link || !nav.contains(link)) {
            return;
        }

        const activeItem = this.getItems(nav).find((item) => item.link === link);

        if (!activeItem) {
            return;
        }

        nav.dataset.pendingTimelineId = activeItem.id;
        nav.dataset.pendingTimelineUntil = String(Date.now() + 1500);
        this.setActiveItem(nav, activeItem, true);

        window.setTimeout(() => this.requestUpdate(), 100);
        window.setTimeout(() => this.requestUpdate(), 350);
        window.setTimeout(() => this.requestUpdate(), 800);
        window.setTimeout(() => this.requestUpdate(), 1550);
    },

    updateActiveItem(nav) {
        const items = this.getItems(nav);

        if (!items.length) {
            return;
        }

        const pendingItem = items.find((item) => item.id === nav.dataset.pendingTimelineId);
        const pendingUntil = nav.dataset.pendingTimelineUntil
            ? Number(nav.dataset.pendingTimelineUntil)
            : 0;

        if (pendingItem && Date.now() < pendingUntil) {
            this.setActiveItem(nav, pendingItem);
            return;
        }

        delete nav.dataset.pendingTimelineId;
        delete nav.dataset.pendingTimelineUntil;

        const navBottom = nav.getBoundingClientRect().bottom;
        const activeOffset = Math.max(navBottom, nav.offsetHeight) + 16;
        const activeY = window.scrollY + activeOffset;
        let activeItem = items[0];

        items.forEach((item) => {
            const targetTop = item.target.getBoundingClientRect().top + window.scrollY;
            const scrollMarginTop = parseFloat(window.getComputedStyle(item.target).scrollMarginTop) || 0;

            if (targetTop - scrollMarginTop <= activeY) {
                activeItem = item;
            }
        });

        this.setActiveItem(nav, activeItem);
    },

    setActiveItem(nav, activeItem, forceScroll = false) {
        const items = this.getItems(nav);

        items.forEach((item) => {
            const isActive = item.id === activeItem.id;

            item.link.classList.toggle('is-active', isActive);

            if (isActive) {
                item.link.setAttribute('aria-current', 'true');
            } else {
                item.link.removeAttribute('aria-current');
            }
        });

        if (forceScroll || nav.dataset.activeTimelineId !== activeItem.id) {
            nav.dataset.activeTimelineId = activeItem.id;
            this.scrollActiveItemIntoView(nav, activeItem.link);
        }

        this.setActiveHeading(nav, activeItem);
        this.setOverflowState(nav);
    },

    setActiveHeading(nav, activeItem) {
        this.getItems(nav).forEach((item) => {
            const heading = item.target.closest('.timeline-heading-block');

            if (heading) {
                heading.classList.toggle('is-active', item.id === activeItem.id);
            }
        });
    },

    updateTimelineProgress(nav) {
        const pageBuilder = nav.closest('.page-builder');

        if (!pageBuilder) {
            return;
        }

        const progressY = Math.max(nav.getBoundingClientRect().bottom + 16, window.innerHeight * 0.42);
        const timelineBlocks = pageBuilder.querySelectorAll('.timeline-heading-block, .content-columns-block--timeline');

        timelineBlocks.forEach((block) => {
            const rect = block.getBoundingClientRect();
            const segmentTop = block.classList.contains('timeline-heading-block')
                ? rect.top + (rect.height / 2)
                : rect.top;
            const segmentHeight = block.classList.contains('timeline-heading-block')
                ? rect.height / 2
                : rect.height;
            const progress = Math.min(Math.max((progressY - segmentTop) / Math.max(segmentHeight, 1), 0), 1);

            block.style.setProperty('--timeline-line-progress', `${progress * 100}%`);

            if (block.classList.contains('content-columns-block--timeline')) {
                block.classList.toggle('is-year-active', progress > 0.02);
            } else if (block.classList.contains('timeline-heading-block')) {
                block.classList.toggle('is-line-active', progress > 0.001);
            }
        });
    },

    scrollActiveItemIntoView(nav, link) {
        const scroller = this.getScroller(nav);

        if (!scroller || scroller.scrollWidth <= scroller.clientWidth) {
            return;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        const gutter = 24;
        const isSmallScreen = window.matchMedia(this.stackedViewportQuery).matches;
        let left = scroller.scrollLeft;

        if (isSmallScreen) {
            if (linkRect.left < scrollerRect.left + gutter) {
                left -= scrollerRect.left + gutter - linkRect.left;
            } else if (linkRect.right > scrollerRect.right - gutter) {
                left += linkRect.right - (scrollerRect.right - gutter);
            } else {
                return;
            }
        } else {
            left += linkRect.left - scrollerRect.left - ((scroller.clientWidth - linkRect.width) / 2);
        }

        scroller.scrollTo({
            left: Math.max(0, left),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
    },

    requestUpdate() {
        if (this.ticking) {
            return;
        }

        window.requestAnimationFrame(() => {
            this.update();
            this.ticking = false;
        });

        this.ticking = true;
    },

    update() {
        const scrollY = window.scrollY;

        this.navs.forEach((nav) => {
            const triggerTop = nav.dataset.triggerTop
                ? Number(nav.dataset.triggerTop)
                : 0;

            const isPastTrigger = scrollY >= triggerTop;

            nav.classList.toggle('is-stuck', isPastTrigger);
            this.setOverflowState(nav);
            this.updateActiveItem(nav);
            this.updateTimelineProgress(nav);
        });
    },
};

export default pageTimelineNav;
